// src/pages/enterprise/EnterpriseTravelChannelTab.js
//
// GOV-TRAVEL-CHANNEL-PANEL-01, Part B. The "Travel channel" tab: how this customer's flights get
// booked (organization.travelChannel, the spec's section 5,
// docs/specs/UNITED_GOV_TRAVEL_ROUTING_LAYER_SPEC_v1_2026-10-03.md in org-backend). Every field
// of the record, each outside fact with its source url and found-on date.
//
//   GET  {T6_BASE}/api/enterprises/:orgId/travel-channel           { channel, meta, vocabulary }
//   PUT  {T6_BASE}/api/enterprises/:orgId/travel-channel           the record
//   GET  {T6_BASE}/api/enterprises/:orgId/travel-channel/history   the panel's audit (who, when, diff)
//   POST {T6_BASE}/api/enterprises/:orgId/travel-channel/test-deeplink  { trip, channel }
//   POST {T6_BASE}/api/enterprises/:orgId/travel-channel/test-email     { trip, channel, sendTo }
//
// The lists the form offers (airlines and the fields Duffel takes for each, adapters and their
// one line, the value lists) come from org-backend's config through the GET's vocabulary; this
// file keeps no copy. Save stays off until every outside fact has a source (Rule 1b, the
// contract's own list in travelChannelForm.missingSources). org-backend's refusals are shown as
// readable lines next to the field and all together above Save (Rule 3). The two test buttons
// preview what is on screen, saved or not; nothing is booked and the test email goes only to the
// typed address.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    IconButton,
    Skeleton,
    Alert,
    Snackbar,
    CircularProgress,
    Link as MuiLink,
} from '@mui/material';
import { styled } from '@mui/system';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';

import { T6_BASE, authHeader } from './EnterpriseAgentAccessTab';
import {
    toForm,
    fromForm,
    missingSources,
    refusalLine,
    privateFarePath,
    sourceComplete,
    newPrivateFare,
    newRateRule,
    newDirectBooking,
    newPolicyLink,
} from './travelChannelForm';

// -----------------------------------------------------------------------------
//  STYLED  (matches EnterpriseTravelTab)
// -----------------------------------------------------------------------------

const SectionCard = styled(Card)({
    backgroundColor: '#111',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
    borderRadius: 14,
    padding: '0.8rem',
    marginBottom: '1.5rem',
});

const inputSx = {
    backgroundColor: '#161616',
    '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: '#333' },
        '&:hover fieldset': { borderColor: '#444' },
        '&.Mui-focused fieldset': { borderColor: '#12c099' },
    },
    '& input, & select': { color: '#fff' },
    '& select option': { backgroundColor: '#161616' },
    '& .MuiInputLabel-root': { color: '#888' },
};

const mintButtonSx = {
    backgroundColor: '#12c099',
    color: '#000',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#0ea884' },
};

const outlineButtonSx = {
    borderColor: '#333',
    color: '#fff',
    textTransform: 'none',
    '&:hover': { borderColor: '#12c099', color: '#12c099' },
};

const errorAlertSx = { bgcolor: 'rgba(255,107,107,0.1)', color: '#ff6b6b', border: '1px solid rgba(255,107,107,0.3)' };

// -----------------------------------------------------------------------------
//  WORDS
// -----------------------------------------------------------------------------

const MODE_TEXT = {
    united_books: {
        label: 'United books',
        line: 'United searches, charges the wallet and books the ticket itself. Nothing below is used except the contract airline codes, which every search sends.',
    },
    platform_of_record: {
        label: 'Platform of record',
        line: 'United finds the flight and hands it to this customer\'s own booking tool or travel agency, which books it. United charges nothing and never uses the wallet.',
    },
};

const LABELS = {
    concur: 'Concur',
    tmc_tool: 'Agency booking tool',
    agent_only: 'Agency by phone or email only',
    federal_ets: 'Federal E-Gov Travel (ETS)',
    none: 'None',
    out_of_state: 'Out of state trips',
    all: 'All trips',
    concur_request: 'Concur Request',
    form: 'A form',
    state_travel_card: 'State travel card',
    agency_direct_bill: 'Agency direct bill',
    wallet: 'United wallet',
    fixed: 'Fixed contract fare ($)',
    percent: 'Percent off',
    exists: 'Contract fare exists, amount unknown',
    concur_deeplink: 'Concur deep link',
    tmc_agent_email: 'Agency email',
    agent_phone: 'Agency phone',
};
const label = (v) => LABELS[v] || v;

const REASONS = {
    platform_not_concur: 'the platform of record is not Concur',
    deep_link_not_supported_on_record: 'deep links are not marked supported',
    packet_has_no_search: 'the sample trip has no search',
    no_agency_email_on_record: 'no agency email is on the record',
    agency_email_unsourced: 'the agency email has no source',
    no_agency_phone_on_record: 'no agency phone is on the record',
    agency_phone_unsourced: 'the agency phone has no source',
    email_not_configured: 'org-backend has no SendGrid key',
    not_in_record_order: 'it is not in the adapter order',
    adapter_not_registered: 'org-backend has no such adapter',
};
const reasonText = (r) => REASONS[r] || r;

const TEST_EMAIL_LINES = {
    TRAVEL_CHANNEL_TEST_TO_AGENCY: 'That is the agency\'s own address. A test packet never goes to the agency.',
    TRAVEL_CHANNEL_TEST_TO_INVALID: 'That is not an email address.',
    TRAVEL_CHANNEL_TEST_EMAIL_NOT_CONFIGURED: 'org-backend has no SendGrid key, so nothing was sent.',
    TRAVEL_CHANNEL_TEST_EMAIL_FAILED: 'SendGrid refused the send, so nothing was sent.',
    PANEL_TEST_EMAIL_TO_MISSING: 'Type the address the test packet goes to.',
};

// One line for a failed request, from the body the panel passed through unchanged.
function requestErrorLine(err, fallback) {
    const d = err && err.response && err.response.data;
    if (d && d.code === 'TRAVEL_CHANNEL_PREVIEW_TRIP_INVALID' && Array.isArray(d.errors)) {
        return 'Check the sample trip: ' + d.errors.map((e) => e.path.replace(/^trip\./, '') + ' needs ' + e.rule).join('; ') + '.';
    }
    if (d && TEST_EMAIL_LINES[d.code]) return TEST_EMAIL_LINES[d.code] + (d.message ? ' (' + d.message + ')' : '');
    if (d && (d.message || d.code || d.error)) return d.message || refusalLine({ code: d.code || d.error });
    return (err && err.message) || fallback;
}

// -----------------------------------------------------------------------------
//  HELPERS
// -----------------------------------------------------------------------------

// Immutable set at a dotted path with [i] indices: setIn(f, 'rateRules[0].source.url', 'x').
function setIn(obj, path, value) {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    const go = (o, i) => {
        const k = keys[i];
        const isIdx = /^\d+$/.test(k);
        const base = o == null ? (isIdx ? [] : {}) : o;
        const copy = Array.isArray(base) ? base.slice() : { ...base };
        copy[isIdx ? Number(k) : k] = i === keys.length - 1 ? value : go(base[isIdx ? Number(k) : k], i + 1);
        return copy;
    };
    return go(obj, 0);
}

const fmtWhen = (v) => {
    const d = v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : null;
};
const fmtValue = (v) => (v == null ? 'empty' : Array.isArray(v) ? (v.length ? v.join(', ') : 'none') : typeof v === 'object' ? (Object.keys(v).length ? JSON.stringify(v) : 'none') : String(v));

const EMPTY_TRIP = { from: '', to: '', departureDate: '', returnDate: '' };

// -----------------------------------------------------------------------------
//  SMALL PIECES
// -----------------------------------------------------------------------------

function Field({ label: text, path, value, onChange, type = 'text', options = null, width = 220, disabled = false, placeholder }) {
    return (
        <TextField
            label={text}
            size="small"
            type={type}
            value={value == null ? '' : value}
            onChange={(e) => onChange(path, e.target.value)}
            select={!!options}
            SelectProps={options ? { native: true } : undefined}
            inputProps={{ 'data-path': path }}
            InputLabelProps={{ shrink: true }}
            disabled={disabled}
            placeholder={placeholder}
            sx={{ ...inputSx, width }}
        >
            {options && options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </TextField>
    );
}

// Options for a native select from a vocabulary list, keeping a stored value it does not know.
const optionsOf = (list, current, { blank = null } = {}) => {
    const opts = (list || []).map((v) => ({ value: v, label: label(v) }));
    if (current && !(list || []).includes(current)) opts.push({ value: current, label: current + ' (not in org-backend\'s list)' });
    return blank != null ? [{ value: '', label: blank }, ...opts] : opts;
};

function Lines({ lines }) {
    if (!lines || !lines.length) return null;
    return (
        <Box sx={{ mt: 0.5 }}>
            {lines.map((l, i) => (
                <Typography key={i} data-refusal="1" sx={{ color: '#ff6b6b', fontSize: '0.8rem' }}>{l}</Typography>
            ))}
        </Box>
    );
}

function SourceFields({ path, value, onChange, required, lines }) {
    const missing = required && !sourceComplete(value);
    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Field label="Source url" path={path + '.url'} value={value.url} onChange={onChange} width={320} placeholder="the page this came from" />
                <Field label="Found on" path={path + '.foundAt'} value={value.foundAt} onChange={onChange} type="date" width={170} />
                {missing && (
                    <Typography data-missing-source={path} sx={{ color: '#ffb84d', fontSize: '0.8rem' }}>
                        Needs a source url and the date it was read
                    </Typography>
                )}
            </Box>
            <Lines lines={lines} />
        </Box>
    );
}

function Section({ title, children, note }) {
    return (
        <SectionCard>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>{title}</Typography>
                {note && <Typography sx={{ color: '#888', fontSize: '0.85rem', mb: 1 }}>{note}</Typography>}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                {children}
            </CardContent>
        </SectionCard>
    );
}

const Row = ({ children }) => <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>{children}</Box>;

// -----------------------------------------------------------------------------
//  COMPONENT
// -----------------------------------------------------------------------------

export default function EnterpriseTravelChannelTab({ orgId }) {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saved, setSaved] = useState(null);          // the record as org-backend last answered it
    const [meta, setMeta] = useState(null);
    const [voc, setVoc] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [refusal, setRefusal] = useState(null);      // { code, message, errors: [{ code, path, detail }] }
    const [history, setHistory] = useState({ entries: null, error: null });
    const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

    const [trip, setTrip] = useState(EMPTY_TRIP);
    const [preview, setPreview] = useState({ busy: false, result: null, error: null });
    const [testTo, setTestTo] = useState('');
    const [testEmail, setTestEmail] = useState({ busy: false, result: null, error: null });
    const [copied, setCopied] = useState(false);

    const base = `${T6_BASE}/api/enterprises/${orgId}/travel-channel`;

    const fetchHistory = useCallback(async () => {
        try {
            const headers = await authHeader();
            const res = await axios.get(`${base}/history`, { headers });
            setHistory({ entries: Array.isArray(res.data && res.data.entries) ? res.data.entries : [], error: null });
        } catch (err) {
            setHistory({ entries: null, error: requestErrorLine(err, 'The save history could not be read.') });
        }
    }, [base]);

    const fetchChannel = useCallback(async () => {
        if (!orgId) {
            setLoadError('No organization selected. Open this page from the Enterprises list.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const headers = await authHeader();
            const res = await axios.get(base, { headers });
            const d = res.data || {};
            if (!d.vocabulary) throw new Error('org-backend answered without its vocabulary, so the editor cannot offer the right fields. Deploy org-backend (t1) with GOV-TRAVEL-CHANNEL-PANEL-01 first.');
            setSaved(d.channel);
            setMeta(d.meta || null);
            setVoc(d.vocabulary);
            setForm(toForm(d.channel));
            setRefusal(null);
        } catch (err) {
            setLoadError(requestErrorLine(err, 'Failed to load the travel channel.'));
        } finally {
            setLoading(false);
        }
        fetchHistory();
    }, [orgId, base, fetchHistory]);

    useEffect(() => {
        fetchChannel();
    }, [fetchChannel]);

    const set = useCallback((path, value) => setForm((f) => setIn(f, path, value)), []);

    // ---------------- derived ----------------
    const missing = useMemo(() => (form ? missingSources(form) : []), [form]);
    const dirty = useMemo(() => !!(form && saved && JSON.stringify(fromForm(form)) !== JSON.stringify(fromForm(toForm(saved)))), [form, saved]);
    const linesAt = useCallback((prefix, { exact = false } = {}) => {
        const errs = (refusal && refusal.errors) || [];
        return errs.filter((e) => e.path === prefix || (!exact && (String(e.path).startsWith(prefix + '.') || String(e.path).startsWith(prefix + '[')))).map(refusalLine);
    }, [refusal]);

    // ---------------- save ----------------
    const handleSave = async () => {
        setSaving(true);
        setRefusal(null);
        try {
            const headers = await authHeader();
            const res = await axios.put(base, fromForm(form), { headers });
            const d = res.data || {};
            setSaved(d.channel);
            setForm(toForm(d.channel));
            setToast({ open: true, severity: 'success', message: 'Travel channel saved.' });
            fetchChannel();
        } catch (err) {
            const d = (err.response && err.response.data) || {};
            setRefusal({ code: d.code || d.error || null, message: requestErrorLine(err, 'The save failed.'), errors: Array.isArray(d.errors) ? d.errors : [] });
        } finally {
            setSaving(false);
        }
    };

    // ---------------- tests ----------------
    const tripReady = /^[A-Za-z]{3}$/.test(trip.from.trim()) && /^[A-Za-z]{3}$/.test(trip.to.trim()) && !!trip.departureDate;
    const tripBody = () => ({ from: trip.from.trim().toUpperCase(), to: trip.to.trim().toUpperCase(), departureDate: trip.departureDate, ...(trip.returnDate ? { returnDate: trip.returnDate } : {}) });

    const handlePreview = async () => {
        setPreview({ busy: true, result: null, error: null });
        setCopied(false);
        try {
            const headers = await authHeader();
            const res = await axios.post(`${base}/test-deeplink`, { trip: tripBody(), channel: fromForm(form) }, { headers });
            setPreview({ busy: false, result: res.data, error: null });
        } catch (err) {
            setPreview({ busy: false, result: null, error: requestErrorLine(err, 'The preview failed.') });
        }
    };

    const handleTestEmail = async () => {
        setTestEmail({ busy: true, result: null, error: null });
        try {
            const headers = await authHeader();
            const res = await axios.post(`${base}/test-email`, { trip: tripBody(), channel: fromForm(form), sendTo: testTo.trim() }, { headers });
            setTestEmail({ busy: false, result: res.data, error: null });
        } catch (err) {
            setTestEmail({ busy: false, result: null, error: requestErrorLine(err, 'The test email failed.') });
        }
    };

    const copyUrl = async (url) => {
        try { await navigator.clipboard.writeText(url); setCopied(true); }
        catch { setCopied(false); setToast({ open: true, severity: 'error', message: 'The browser would not copy. Select the link and copy it by hand.' }); }
    };

    // ---------------- adapter order ----------------
    const adapterRows = useMemo(() => {
        if (!form || !voc) return [];
        const known = voc.adapters.map((a) => a.id);
        const ids = [...form.preferredChannelOrder, ...known.filter((id) => !form.preferredChannelOrder.includes(id))];
        return ids.map((id) => ({ id, meta: voc.adapters.find((a) => a.id === id) || null, on: form.preferredChannelOrder.includes(id) }));
    }, [form, voc]);

    const toggleAdapter = (id) => {
        const order = form.preferredChannelOrder.slice();
        const lastId = (voc.adapters.find((a) => a.last) || {}).id;
        if (order.includes(id)) set('preferredChannelOrder', order.filter((x) => x !== id));
        else if (id === lastId || !order.includes(lastId)) set('preferredChannelOrder', [...order, id]);
        else set('preferredChannelOrder', [...order.filter((x) => x !== lastId), id, lastId]);
    };
    const moveAdapter = (id, dir) => {
        const order = form.preferredChannelOrder.slice();
        const i = order.indexOf(id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= order.length) return;
        [order[i], order[j]] = [order[j], order[i]];
        set('preferredChannelOrder', order);
    };

    const setMode = (mode) => {
        setForm((f) => {
            let next = setIn(f, 'mode', mode);
            if (mode === 'platform_of_record' && next.payment === 'wallet') next = setIn(next, 'payment', '');
            return next;
        });
    };

    const addRow = (key, row) => set(key, [...form[key], row]);
    const removeRow = (key, i) => set(key, form[key].filter((_, j) => j !== i));

    // ---------------- render ----------------
    const header = (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Travel channel</Typography>
                <Typography sx={{ color: '#888', fontSize: '0.9rem', mt: 0.5, maxWidth: 720 }}>
                    How this customer's flights get booked: by United, or on their own booking platform or travel agency. Every outside fact carries the page it came from and the date it was read.
                </Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={fetchChannel} disabled={loading} sx={{ ...outlineButtonSx, flexShrink: 0 }}>
                Refresh
            </Button>
        </Box>
    );

    if (loading && !form) {
        return (
            <Box>
                {header}
                <SectionCard><CardContent>
                    <Skeleton variant="text" width="30%" sx={{ bgcolor: '#222' }} />
                    <Skeleton variant="rounded" height={80} sx={{ bgcolor: '#1a1a1a', mt: 2 }} />
                </CardContent></SectionCard>
            </Box>
        );
    }

    if (loadError || !form || !voc) {
        return (
            <Box>
                {header}
                <Alert severity="error" sx={errorAlertSx} action={<Button color="inherit" size="small" onClick={fetchChannel}>Retry</Button>}>
                    {loadError || 'The travel channel could not be loaded.'}
                </Alert>
            </Box>
        );
    }

    const platformMode = form.mode === 'platform_of_record';
    const airlineSpec = (code) => voc.privateFareAirlines.find((a) => a.code === String(code || '').toUpperCase()) || null;
    const paymentOptions = optionsOf(voc.payments.filter((p) => !(platformMode && p === 'wallet')), platformMode && form.payment === 'wallet' ? null : form.payment, { blank: 'Not set' });
    const latest = history.entries && history.entries[0];

    return (
        <Box>
            {header}

            {/* LAST SAVE (B3) */}
            <SectionCard>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>Last save</Typography>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                    {meta && (meta.updatedAt || meta.updatedBy) ? (
                        <Typography data-last-save="1" sx={{ color: '#ddd' }}>
                            Saved by {meta.updatedBy || 'someone unnamed'}{fmtWhen(meta.updatedAt) ? ' on ' + fmtWhen(meta.updatedAt) : ''}.
                        </Typography>
                    ) : (
                        <Typography data-last-save="1" sx={{ color: '#aaa' }}>Never saved. This customer reads as United books.</Typography>
                    )}
                    {history.error && <Typography sx={{ color: '#ffb84d', fontSize: '0.85rem', mt: 1 }}>{history.error} The last save above is org-backend's own record.</Typography>}
                    {latest && (
                        <Box sx={{ mt: 1.5 }} data-history-latest="1">
                            <Typography sx={{ color: '#888', fontSize: '0.85rem', mb: 0.5 }}>
                                What changed in the panel's last save ({(latest.by && latest.by.email) || 'unknown'}, {fmtWhen(latest.at) || 'time unknown'}):
                            </Typography>
                            {latest.changes.length ? latest.changes.map((c) => (
                                <Typography key={c.path} data-diff-path={c.path} sx={{ fontSize: '0.85rem', color: '#ccc', fontFamily: 'monospace' }}>
                                    {c.path}: {fmtValue(c.before)} → {fmtValue(c.after)}
                                </Typography>
                            )) : <Typography sx={{ fontSize: '0.85rem', color: '#aaa' }}>Nothing changed.</Typography>}
                            {history.entries.slice(1, 6).map((e, i) => (
                                <Typography key={i} sx={{ fontSize: '0.8rem', color: '#777', mt: 0.5 }}>
                                    Earlier: {(e.by && e.by.email) || 'unknown'}, {fmtWhen(e.at) || 'time unknown'}, {e.changes.length} change{e.changes.length === 1 ? '' : 's'}.
                                </Typography>
                            ))}
                        </Box>
                    )}
                    {history.entries && !history.entries.length && meta && meta.updatedAt && (
                        <Typography sx={{ color: '#888', fontSize: '0.85rem', mt: 1 }}>That save was made outside the panel, so the panel kept no diff of it.</Typography>
                    )}
                </CardContent>
            </SectionCard>

            {/* MODE */}
            <Section title="Mode">
                <RadioGroup value={form.mode} onChange={(e) => setMode(e.target.value)}>
                    {voc.modes.map((m) => (
                        <Box key={m} sx={{ mb: 1 }}>
                            <FormControlLabel
                                value={m}
                                control={<Radio inputProps={{ 'data-path': 'mode:' + m }} sx={{ color: '#666', '&.Mui-checked': { color: '#12c099' } }} />}
                                label={(MODE_TEXT[m] && MODE_TEXT[m].label) || m}
                            />
                            <Typography sx={{ color: '#888', fontSize: '0.85rem', ml: 4 }}>{MODE_TEXT[m] && MODE_TEXT[m].line}</Typography>
                        </Box>
                    ))}
                </RadioGroup>
                <Lines lines={linesAt('mode')} />
            </Section>

            {/* PLATFORM OF RECORD */}
            <Section title="Platform of record">
                <Row>
                    <Field label="Kind" path="platformOfRecord.kind" value={form.platformOfRecord.kind} onChange={set} options={optionsOf(voc.platformKinds, form.platformOfRecord.kind, { blank: 'Not set' })} />
                    <Field label="Name" path="platformOfRecord.name" value={form.platformOfRecord.name} onChange={set} />
                    <Field label="Url" path="platformOfRecord.url" value={form.platformOfRecord.url} onChange={set} width={300} />
                    <Field label="Concur company UUID" path="platformOfRecord.companyUuid" value={form.platformOfRecord.companyUuid} onChange={set} width={330} />
                </Row>
                <Typography sx={{ color: '#aaa', fontSize: '0.9rem' }}>Deep links supported</Typography>
                <RadioGroup row value={form.platformOfRecord.deepLinkSupported} onChange={(e) => set('platformOfRecord.deepLinkSupported', e.target.value)}>
                    {['yes', 'no', 'unknown'].map((v) => (
                        <FormControlLabel key={v} value={v} label={v === 'yes' ? 'Yes' : v === 'no' ? 'No' : 'Unknown'}
                            control={<Radio inputProps={{ 'data-path': 'platformOfRecord.deepLinkSupported:' + v }} sx={{ color: '#666', '&.Mui-checked': { color: '#12c099' } }} />} />
                    ))}
                </RadioGroup>
                <Typography sx={{ color: '#888', fontSize: '0.8rem', mb: 1, maxWidth: 760 }}>
                    Concur's deep link opens a pre-filled search and cannot pick the flight. It works only when the company is on the new Concur Travel and the traveller is signed in. Mark yes only once that is confirmed.{' '}
                    {voc.concurSource && <MuiLink href={voc.concurSource.url} target="_blank" rel="noreferrer" sx={{ color: '#12c099' }}>Concur's page, read {voc.concurSource.foundAt}</MuiLink>}
                </Typography>
                <SourceFields path="platformOfRecord.source" value={form.platformOfRecord.source} onChange={set}
                    required={!!(form.platformOfRecord.name.trim() || form.platformOfRecord.url.trim())} lines={linesAt('platformOfRecord')} />
            </Section>

            {/* TRAVEL AGENCY */}
            <Section title="Travel agency" note="One source covers every agency fact here (the record keeps one). Use the page that states them.">
                <Row>
                    <Field label="Name" path="tmc.name" value={form.tmc.name} onChange={set} width={280} />
                    <Field label="Agent email" path="tmc.agentEmail" value={form.tmc.agentEmail} onChange={set} width={280} />
                    <Field label="Phone" path="tmc.agentPhone" value={form.tmc.agentPhone} onChange={set} />
                    <Field label="Hours" path="tmc.hours" value={form.tmc.hours} onChange={set} width={260} />
                </Row>
                <Row>
                    <Field label="Fee online ($)" path="tmc.feesUSD.online" value={form.tmc.feesUSD.online} onChange={set} type="number" width={160} />
                    <Field label="Fee agent domestic ($)" path="tmc.feesUSD.agentDomestic" value={form.tmc.feesUSD.agentDomestic} onChange={set} type="number" width={190} />
                    <Field label="Fee agent international ($)" path="tmc.feesUSD.agentInternational" value={form.tmc.feesUSD.agentInternational} onChange={set} type="number" width={210} />
                </Row>
                <SourceFields path="tmc.source" value={form.tmc.source} onChange={set}
                    required={missing.some((m) => m.path === 'tmc.source')} lines={linesAt('tmc')} />
            </Section>

            {/* TRAVEL REQUEST */}
            <Section title="Travel request rule">
                <Row>
                    <Field label="Required for" path="travelRequest.required" value={form.travelRequest.required} onChange={set} options={optionsOf(voc.travelRequestRequired, form.travelRequest.required)} />
                    <Field label="System" path="travelRequest.system" value={form.travelRequest.system} onChange={set} options={optionsOf(voc.travelRequestSystems, form.travelRequest.system, { blank: 'Not set' })} />
                    <Field label="Form name" path="travelRequest.formName" value={form.travelRequest.formName} onChange={set} />
                    <Field label="Url" path="travelRequest.url" value={form.travelRequest.url} onChange={set} width={320} />
                </Row>
                <SourceFields path="travelRequest.source" value={form.travelRequest.source} onChange={set} required={!!form.travelRequest.url.trim()} lines={linesAt('travelRequest')} />
            </Section>

            {/* ADAPTER ORDER */}
            <Section title="Adapter order" note="Tried in this order for each trip; each one says whether it can serve. Agent phone is a card, never a booking, and always last.">
                {adapterRows.map((r, i) => (
                    <Box key={r.id} data-adapter={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, opacity: r.on ? 1 : 0.6 }}>
                        <Checkbox checked={r.on} onChange={() => toggleAdapter(r.id)} inputProps={{ 'data-path': 'adapter:' + r.id }} sx={{ color: '#666', '&.Mui-checked': { color: '#12c099' } }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600 }}>{r.on ? (form.preferredChannelOrder.indexOf(r.id) + 1) + '. ' : ''}{label(r.id)} <span style={{ color: '#666', fontWeight: 400 }}>{r.id}</span></Typography>
                            <Typography data-adapter-description={r.id} sx={{ color: '#888', fontSize: '0.85rem' }}>{r.meta ? r.meta.description : 'org-backend does not list this adapter.'}</Typography>
                        </Box>
                        <IconButton size="small" aria-label={'move ' + r.id + ' up'} disabled={!r.on || form.preferredChannelOrder.indexOf(r.id) === 0} onClick={() => moveAdapter(r.id, -1)} sx={{ color: '#888' }}><ArrowUpwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" aria-label={'move ' + r.id + ' down'} disabled={!r.on || form.preferredChannelOrder.indexOf(r.id) === form.preferredChannelOrder.length - 1} onClick={() => moveAdapter(r.id, 1)} sx={{ color: '#888' }}><ArrowDownwardIcon fontSize="small" /></IconButton>
                    </Box>
                ))}
                <Lines lines={linesAt('preferredChannelOrder')} />
            </Section>

            {/* CONTRACT AIRLINES */}
            <Section title="Contract airlines" note={
                <>Duffel passes contract codes for these airlines only, and only the fields it takes for each.{' '}
                    {voc.privateFaresSource && <MuiLink href={voc.privateFaresSource.url} target="_blank" rel="noreferrer" sx={{ color: '#12c099' }}>Duffel's page, read {voc.privateFaresSource.foundAt}</MuiLink>}</>
            }>
                {form.privateFares.map((row, i) => {
                    const spec = airlineSpec(row.airline);
                    const fields = spec ? ['corporateCode', 'tourCode'].filter((f) => spec.requires.includes(f) || spec.expects.includes(f)) : [];
                    const p = 'privateFares[' + i + ']';
                    const cpath = privateFarePath(form.privateFares, i);
                    return (
                        <Box key={i} data-private-fare-row={i} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 1.5, mb: 1.5 }}>
                            <Row>
                                <Field label="Airline" path={p + '.airline'} value={row.airline} onChange={set}
                                    options={[{ value: '', label: 'Pick an airline' }, ...voc.privateFareAirlines.map((a) => ({ value: a.code, label: a.code + ', ' + a.name })),
                                        ...(row.airline && !spec ? [{ value: row.airline, label: row.airline + ' (Duffel takes no codes for it)' }] : [])]} width={260} />
                                {fields.map((f) => (
                                    <Field key={f} label={(f === 'corporateCode' ? 'Corporate code' : 'Tour code') + (spec.requires.includes(f) ? ' (required)' : ' (expected)')}
                                        path={p + '.' + f} value={row[f]} onChange={set} />
                                ))}
                                {spec && !fields.includes('tourCode') && <Typography sx={{ color: '#777', fontSize: '0.8rem', alignSelf: 'center' }}>Duffel takes no tour code for {spec.code}.</Typography>}
                                <IconButton aria-label={'remove contract airline row ' + (i + 1)} onClick={() => removeRow('privateFares', i)} sx={{ color: '#888' }}><DeleteOutlineIcon /></IconButton>
                            </Row>
                            <SourceFields path={p + '.source'} value={row.source} onChange={set} required lines={linesAt(cpath)} />
                        </Box>
                    );
                })}
                {/* An airline Duffel does not take is refused at privateFares.<code>, not at a row. */}
                <Lines lines={((refusal && refusal.errors) || []).filter((e) => /^privateFares\.[^.[]+$/.test(e.path)).map(refusalLine)} />
                <Button startIcon={<AddIcon />} onClick={() => addRow('privateFares', newPrivateFare())} sx={{ ...outlineButtonSx, border: '1px solid #333' }}>Add contract airline</Button>
            </Section>

            {/* RATE RULES */}
            <Section title="Rate rules" note="Contract rates Duffel cannot show: the card names them beside the public fare and the booking packet asks the agency to apply them.">
                {form.rateRules.map((r, i) => {
                    const p = 'rateRules[' + i + ']';
                    return (
                        <Box key={i} data-rate-rule-row={i} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 1.5, mb: 1.5 }}>
                            <Row>
                                <Field label="Airline (2 letters)" path={p + '.airline'} value={r.airline} onChange={set} width={140} />
                                <Field label="Origin" path={p + '.origin'} value={r.origin} onChange={set} width={110} placeholder="any" />
                                <Field label="Destination" path={p + '.destination'} value={r.destination} onChange={set} width={120} placeholder="any" />
                                <Field label="Rule" path={p + '.kind'} value={r.kind} onChange={set} options={optionsOf(voc.rateRuleKinds, r.kind, { blank: 'Pick a rule' })} width={290} />
                                <Field label="Value" path={p + '.value'} value={r.value} onChange={set} type="number" width={120} disabled={r.kind === 'exists' || !r.kind} />
                                <IconButton aria-label={'remove rate rule ' + (i + 1)} onClick={() => removeRow('rateRules', i)} sx={{ color: '#888' }}><DeleteOutlineIcon /></IconButton>
                            </Row>
                            <SourceFields path={p + '.source'} value={r.source} onChange={set} required lines={linesAt(p)} />
                        </Box>
                    );
                })}
                <Button startIcon={<AddIcon />} onClick={() => addRow('rateRules', newRateRule())} sx={{ ...outlineButtonSx, border: '1px solid #333' }}>Add rate rule</Button>
            </Section>

            {/* DIRECT BOOKING */}
            <Section title="Direct booking rules" note="Airlines this customer books directly, outside the platform, and how.">
                {form.directBooking.map((r, i) => {
                    const p = 'directBooking[' + i + ']';
                    return (
                        <Box key={i} data-direct-booking-row={i} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 1.5, mb: 1.5 }}>
                            <Row>
                                <Field label="Airline" path={p + '.airline'} value={r.airline} onChange={set} width={140} />
                                <Field label="Method" path={p + '.method'} value={r.method} onChange={set} />
                                <Field label="Detail" path={p + '.detail'} value={r.detail} onChange={set} width={320} />
                                <IconButton aria-label={'remove direct booking rule ' + (i + 1)} onClick={() => removeRow('directBooking', i)} sx={{ color: '#888' }}><DeleteOutlineIcon /></IconButton>
                            </Row>
                            <SourceFields path={p + '.source'} value={r.source} onChange={set} required lines={linesAt(p)} />
                        </Box>
                    );
                })}
                <Button startIcon={<AddIcon />} onClick={() => addRow('directBooking', newDirectBooking())} sx={{ ...outlineButtonSx, border: '1px solid #333' }}>Add direct booking rule</Button>
            </Section>

            {/* PER DIEM */}
            <Section title="Per diem source">
                <Row>
                    <Field label="Source" path="perDiem.source" value={form.perDiem.source} onChange={set} placeholder="gsa" />
                    <Field label="Url" path="perDiem.url" value={form.perDiem.url} onChange={set} width={360} />
                </Row>
            </Section>

            {/* POLICY LINKS */}
            <Section title="Policy links">
                {form.policyLinks.map((r, i) => {
                    const p = 'policyLinks[' + i + ']';
                    return (
                        <Box key={i} data-policy-link-row={i} sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', pb: 1.5, mb: 1.5 }}>
                            <Row>
                                <Field label="Title" path={p + '.title'} value={r.title} onChange={set} width={260} />
                                <Field label="Url" path={p + '.url'} value={r.url} onChange={set} width={360} />
                                <IconButton aria-label={'remove policy link ' + (i + 1)} onClick={() => removeRow('policyLinks', i)} sx={{ color: '#888' }}><DeleteOutlineIcon /></IconButton>
                            </Row>
                            <SourceFields path={p + '.source'} value={r.source} onChange={set} required lines={linesAt(p)} />
                        </Box>
                    );
                })}
                <Button startIcon={<AddIcon />} onClick={() => addRow('policyLinks', newPolicyLink())} sx={{ ...outlineButtonSx, border: '1px solid #333' }}>Add policy link</Button>
            </Section>

            {/* PAYMENT */}
            <Section title="Payment" note={platformMode ? 'The wallet is not offered: a platform of record customer never pays from it.' : null}>
                <Field label="Payment" path="payment" value={platformMode && form.payment === 'wallet' ? '' : form.payment} onChange={set} options={paymentOptions} width={260} />
                <Lines lines={linesAt('payment')} />
            </Section>

            {/* SAVE */}
            <SectionCard>
                <CardContent>
                    {refusal && (
                        <Alert severity="error" sx={{ ...errorAlertSx, mb: 2 }} data-refusal-summary="1">
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>org-backend refused the save{refusal.code ? ' (' + refusal.code + ')' : ''}. Nothing was changed.</Typography>
                            {refusal.errors.length
                                ? refusal.errors.map((e, i) => <Typography key={i} sx={{ fontSize: '0.85rem' }}>{e.path}: {refusalLine(e)}</Typography>)
                                : <Typography sx={{ fontSize: '0.85rem' }}>{refusal.message}</Typography>}
                        </Alert>
                    )}
                    {missing.length > 0 && (
                        <Box sx={{ mb: 2 }} data-missing-summary="1">
                            <Typography sx={{ color: '#ffb84d', fontWeight: 600 }}>
                                Save is off until every outside fact has its source ({missing.length} missing):
                            </Typography>
                            {missing.map((m) => <Typography key={m.path} sx={{ color: '#ffb84d', fontSize: '0.85rem' }}>{m.label}</Typography>)}
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button variant="contained" onClick={handleSave} disabled={saving || missing.length > 0 || !dirty}
                            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#06281f' }} /> : <SaveIcon />}
                            sx={{ ...mintButtonSx, '&.Mui-disabled': { backgroundColor: '#1f3a33', color: '#5f7d75' } }}>
                            {saving ? 'Saving…' : 'Save travel channel'}
                        </Button>
                        {!dirty && <Typography sx={{ color: '#777', fontSize: '0.85rem' }}>No changes to save.</Typography>}
                        {dirty && <Button onClick={() => { setForm(toForm(saved)); setRefusal(null); }} sx={{ color: '#aaa', textTransform: 'none' }}>Discard changes</Button>}
                    </Box>
                </CardContent>
            </SectionCard>

            {/* TESTS (B2) */}
            <Section title="Test the adapters" note="These use what is on screen, saved or not. Nothing is booked, nothing is written, and nothing goes to the agency.">
                <Row>
                    <Field label="From (airport)" path="trip.from" value={trip.from} onChange={(_, v) => setTrip((x) => ({ ...x, from: v }))} width={130} placeholder="SLC" />
                    <Field label="To (airport)" path="trip.to" value={trip.to} onChange={(_, v) => setTrip((x) => ({ ...x, to: v }))} width={130} placeholder="DEN" />
                    <Field label="Departs" path="trip.departureDate" value={trip.departureDate} onChange={(_, v) => setTrip((x) => ({ ...x, departureDate: v }))} type="date" width={170} />
                    <Field label="Returns (optional)" path="trip.returnDate" value={trip.returnDate} onChange={(_, v) => setTrip((x) => ({ ...x, returnDate: v }))} type="date" width={170} />
                </Row>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                    <Button variant="outlined" onClick={handlePreview} disabled={!tripReady || preview.busy} sx={outlineButtonSx}>
                        {preview.busy ? 'Previewing…' : 'Preview deep link'}
                    </Button>
                    <Field label="Send the test packet to" path="testTo" value={testTo} onChange={(_, v) => setTestTo(v)} type="email" width={280} placeholder="you@joinunited.ai" />
                    <Button variant="outlined" onClick={handleTestEmail} disabled={!tripReady || !testTo.trim() || testEmail.busy} sx={outlineButtonSx}>
                        {testEmail.busy ? 'Sending…' : 'Send a test packet'}
                    </Button>
                </Box>

                {preview.error && <Alert severity="error" sx={{ ...errorAlertSx, mb: 2 }}>{preview.error}</Alert>}
                {preview.result && preview.result.deepLink && (
                    <Box data-preview-result="1" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField size="small" value={preview.result.deepLink.url} InputProps={{ readOnly: true }} inputProps={{ 'data-path': 'preview.url' }} sx={{ ...inputSx, flex: 1 }} />
                            <Button startIcon={<ContentCopyIcon />} onClick={() => copyUrl(preview.result.deepLink.url)} sx={outlineButtonSx}>{copied ? 'Copied' : 'Copy'}</Button>
                        </Box>
                        <Typography sx={{ color: preview.result.deepLink.wouldServe ? '#12c099' : '#ffb84d', fontSize: '0.85rem', mt: 0.5 }}>
                            {preview.result.deepLink.wouldServe
                                ? 'The Concur deep link would serve this trip for this customer.'
                                : 'Built for reference, but not used for this customer: ' + reasonText(preview.result.deepLink.reason) + '.'}
                        </Typography>
                        {(preview.result.adapters || []).map((a) => (
                            <Typography key={a.adapter} sx={{ color: '#aaa', fontSize: '0.85rem' }}>
                                {label(a.adapter)}{a.inOrder ? '' : ' (not in the order)'}: {a.canServe ? 'can serve' : 'cannot serve, ' + reasonText(a.reason)}
                            </Typography>
                        ))}
                        {preview.result.runner && (
                            <Typography sx={{ color: '#ddd', fontSize: '0.85rem', mt: 0.5 }}>
                                {preview.result.runner.ok ? 'A trip would lead with ' + label(preview.result.runner.adapter) + '.' : 'No adapter in the order can serve; the traveller would get the travel office card.'}
                            </Typography>
                        )}
                        {preview.result.draftErrors && preview.result.draftErrors.length > 0 && (
                            <Typography sx={{ color: '#ffb84d', fontSize: '0.85rem', mt: 0.5 }}>
                                What is on screen has {preview.result.draftErrors.length} thing{preview.result.draftErrors.length === 1 ? '' : 's'} org-backend would refuse on save; the preview ran anyway.
                            </Typography>
                        )}
                    </Box>
                )}

                {testEmail.error && <Alert severity="error" sx={errorAlertSx}>{testEmail.error}</Alert>}
                {testEmail.result && testEmail.result.email && testEmail.result.email.test && (
                    <Alert severity="success" data-test-email-result="1">
                        Sent to {testEmail.result.email.test.to}{testEmail.result.email.test.messageId ? '. SendGrid message id ' + testEmail.result.email.test.messageId : ''}.
                    </Alert>
                )}
            </Section>

            <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })} sx={{ width: '100%' }}>{toast.message}</Alert>
            </Snackbar>
        </Box>
    );
}
